<template>
  <q-page class="q-px-xl q-pt-xl centered-container-medium">
    <div class="fit row wrap justify-start items-center content-start align-start">
      <div class="col-shrink items-center">
        <span class="text-h4 q-pr-md"> {{ i18n.page_title }}</span>
      </div>

      <div class="col-shrink items-center text-bold">
        <q-select
          :model-value="report_years_selected"
          :options="report_years_available"
          filled
          class="text-h4"
          style="width: 140px"
          @update:model-value="report_years_select"
        />
      </div>

      <q-space />

      <div class="col-shrink items-center">
        <div id="taxfillingcta_slot" />
      </div>

      <div
        v-if="report_requirements_valid && (!report || report.status === 'success')"
        class="col-shrink items-center"
      >
        <q-btn
          unelevated
          no-caps
          icon="las la-sync"
          :label="i18n.recalculate_button_label"
          color="primary"
          @click="report_create(report_years_selected)"
        />
      </div>
    </div>

    <!-- BNC/BIC recognition mode -->
    <div
      v-if="report_requirements_valid"
      class="q-mt-md"
    >
      <q-toggle
        v-model="bnc_at_reception"
        :label="i18n.bnc_recognition_label"
        color="primary"
      />
      <div class="text-caption text-grey-7 q-ml-sm">
        {{ i18n.bnc_recognition_hint }}
      </div>
    </div>

    <!-- Report Requirements -->
    <q-list
      v-if="!report_requirements_valid"
      class="q-mt-lg"
    >
      <q-item
        class="q-my-sm q-mb-lg"
      >
        <q-item-section avatar>
          <q-avatar
            v-if="step_import_status.state == 'todo'"
            color="pastel-green"
            text-color="dark"
          >
            1
          </q-avatar>

          <q-avatar
            v-if="step_import_status.state == 'loading'"
            color="accent"
            text-color="white"
          >
            <q-spinner-ios color="white" />
          </q-avatar>

          <q-avatar
            v-if="step_import_status.state == 'error'"
            color="negative"
            text-color="white"
          >
            <q-icon name="las la-exclamation-triangle" />
          </q-avatar>

          <q-avatar
            v-if="step_import_status.state == 'done'"
            color="positive"
            text-color="white"
          >
            <q-icon name="las la-check" />
          </q-avatar>
        </q-item-section>

        <q-item-section>
          <q-item-label class="text-bold">
            {{ i18n.step_import_label }}
          </q-item-label>
          <q-item-label class="">
            {{ i18n.step_import_description }}
          </q-item-label>
          <q-item-label class="text-muted">
            <span class="text-bold">{{ i18n.steps_status }}</span>
            {{ step_import_status.text }}
          </q-item-label>
        </q-item-section>

        <q-item-section side>
          <Link :href="step_import_action">
            <q-btn
              unelevated
              no-caps
              icon="las la-cloud-upload-alt"
              :label="i18n.step_import_action_label"
              color="primary"
            />
          </Link>
        </q-item-section>
      </q-item>
      <q-item
        class="q-my-sm q-mb-lg"
        :disable="step_subscription_status.state == 'done'"
      >
        <q-item-section avatar>
          <q-avatar
            v-if="step_subscription_status.state == 'todo'"
            color="pastel-green"
            text-color="dark"
          >
            2
          </q-avatar>

          <q-avatar
            v-if="step_subscription_status.state == 'error'"
            color="negative"
            text-color="white"
          >
            <q-icon name="las la-exclamation-triangle" />
          </q-avatar>

          <q-avatar
            v-if="step_subscription_status.state == 'done'"
            color="positive"
            text-color="white"
          >
            <q-icon name="las la-check" />
          </q-avatar>
        </q-item-section>

        <q-item-section>
          <q-item-label class="text-bold">
            {{ i18n.step_subscription_label }}
          </q-item-label>
          <q-item-label class="">
            {{ i18n.step_subscription_description }}
          </q-item-label>
          <q-item-label
            v-if="step_subscription_status.text"
            class="text-muted"
          >
            <span class="text-bold">{{ i18n.steps_status }}</span>
            {{ step_subscription_status.text }}
          </q-item-label>
        </q-item-section>

        <q-item-section side>
          <q-btn
            v-if="step_subscription_status.status == 'error'"
            unelevated
            no-caps
            icon="las la-headset"
            :label="i18n.steps_contact_support"
            color="primary"
            :disable="step_subscription_status.status == 'done'"
            @click="contactUs"
          />

          <Link :href="step_subscription_action">
            <q-btn
              :disable="step_subscription_status.status == 'done'"
              unelevated
              no-caps
              icon="las la-shopping-cart"
              :label="i18n.step_subscription_action_label"
              color="primary"
            />
          </Link>
        </q-item-section>
      </q-item>
    </q-list>

    <template v-else>
      <!-- LOADING -->
      <template v-if="report?.status == 'pending' || report?.status == 'processing'">
        <div class="q-mt-xl">
          <!-- Spinner -->
          <div class="text-center">
            <transition
              appear
              enter-active-class="animated pulse"
              leave-active-class="animated bounceOut"
              :duration="500"
            >
              <q-spinner-puff
                class="inline"
                style="animation-duration: 1000ms !important"
                size="90px"
              />
            </transition>
            <p
              class=" text-bold q-mt-lg"
              style="font-size: 16px"
            >
              {{ i18n.report_loading_message_loading }}
            </p>

            <div
              class="text-center"
            >
              <p class="text-caption">
                {{ i18n.report_loading_message }}
              </p>
            </div>
          </div>
        </div>
      </template>

      <!-- ERROR -->
      <template v-else-if="report?.status == 'error'">
        <q-banner
          class="bg-pastel-red text-dark q-mb-lg q-mt-lg"
          inline-actions
        >
          <template v-slot:avatar>
            <q-icon name="las la-exclamation-triangle" />
          </template>
          {{ i18n.report_error_message }}
        </q-banner>
      </template>

      <!-- DISPLAY REPORT -->
      <template v-else-if="report?.status == 'success'">
        <div
          id="report_section"
          class="row q-col-gutter-md full_with q-mt-md"
          :class="{ 'report-section-with-disclaimer': !report_disclaimer_accepted }"
        >
          <!-- Overlay -->
          <q-card
            v-if="!report_disclaimer_accepted"
            class="q-ml-md q-mt-md"
          >
            <div class="overlay-content">
              <div class="text-h5 q-mb-md">
                {{ i18n.disclaimer_title }}
              </div>

              <q-checkbox
                v-model="report_disclaimer_confirm_accounts"
                :label="i18n.disclaimer_accounts_confirm"
                class="q-mb-sm"
              />

              <q-checkbox
                v-model="report_disclaimer_confirm_categorization"
                :label="i18n.disclaimer_categorization_confirm"
                class="q-mb-md"
              />

              <p class="text-caption text-grey">
                {{ i18n.disclaimer_warning }}
              </p>

              <q-btn
                color="primary"
                :label="i18n.disclaimer_action_accept"
                class="q-mt-md"
                unelevated
                :disable="!report_button_display_enabled"
                @click="report_disclaimer_accepted = true"
              />
            </div>
          </q-card>

          <template v-else>
            <!-- Existing report content -->
            <div
              v-if="report.transaction_uncategorized.count > 0"
              class="col-12"
            >
              <q-banner
                class="bg-pastel-red text-black q-pa-md q-mb-lg"
                dense
              >
                <template v-slot:avatar>
                  <q-icon
                    name="support_agent"
                    class=""
                    size="md"
                    color="black"
                  />
                </template>
                <span v-html="report.transaction_uncategorized.message" />
              </q-banner>
            </div>
            <!-- Plus-value -->
            <div class="col-xs-12 col-sm-6">
              <div
                class="bg-pastel-green q-pa-md"
                style="border-radius: 16px"
              >
                <q-item>
                  <q-item-section avatar>
                    <q-avatar
                      color="black"
                      text-color="white"
                    >
                      <q-icon name="las la-coins" />
                    </q-avatar>
                  </q-item-section>

                  <q-item-section>
                    <q-item-label class="text-bold text-dark">
                      {{ i18n.report_amounts_capital_gain }}
                    </q-item-label>
                    <q-item-label class="">
                      <span class="text-h5 text-dark">{{ report.data.total_taxable_pnl }}</span>
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </div>
            </div>

            <!-- Montant imposition -->
            <div class="col-xs-12 col-sm-6">
              <div
                class="bg-pastel-purple q-pa-md"
                style="border-radius: 16px"
              >
                <q-item>
                  <q-item-section avatar>
                    <q-avatar
                      color="black"
                      text-color="white"
                    >
                      <q-icon name="las la-university" />
                    </q-avatar>
                  </q-item-section>

                  <q-item-section>
                    <q-item-label class="text-bold text-dark">
                      {{ i18n.report_amounts_tax }}
                    </q-item-label>
                    <q-item-label class="">
                      <span class="text-h5 text-primary text-dark">{{ report.data.total_tax }}</span>
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </div>
            </div>

            <!-- Résumé -->
            <div class="col-12">
              <div class="bg-casual-card q-pt-md q-pb-lg">
                <q-item dense>
                  <q-item-section>
                    <q-item-label class="text-bold message-box-text">
                      {{ i18n.report_amounts_portfolio }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-item-label class="text-bold message-box-text text-bold">
                      {{ report.data.total_wallet_value }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-item dense>
                  <q-item-section>
                    <q-item-label class="text-bold message-box-text">
                      {{ i18n.report_amounts_taxable }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-item-label class="text-bold message-box-text text-bold">
                      {{ report.data.total_taxable_transactions }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-item dense>
                  <q-item-section>
                    <q-item-label class="text-bold message-box-text">
                      {{ i18n.report_amounts_acquisition }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-item-label class="text-bold message-box-text text-bold">
                      {{ report.data.net_total_buy_price }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-item dense>
                  <q-item-section>
                    <q-item-label class="text-bold message-box-text">
                      {{ i18n.report_amounts_bic }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-item-label class="text-bold message-box-text text-bold">
                      {{ report.data.bic }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-item dense>
                  <q-item-section>
                    <q-item-label class="text-bold message-box-text">
                      {{ i18n.report_amounts_bnc }}
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <q-item-label class="text-bold message-box-text text-bold">
                      {{ report.data.bnc }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </div>
            </div>
          </template>
        </div>

        <q-card class="q-mt-md">
          <q-card-section class="row items-start justify-space-between">
            <div class="col">
              <span class="q-card-title">{{ i18n.documents_title }}</span>
            </div>
          </q-card-section>

          <q-card-section class="q-pt-none">
            <q-list>
              <!-- Bilan général d'activité -->
              <q-item>
                <q-item-section avatar>
                  <q-avatar
                    color="pastel-blue"
                    text-color="dark"
                  >
                    <q-icon name="las la-file-alt" />
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-bold">
                    {{ i18n.documents_activity_label }}
                  </q-item-label>
                  <q-item-label caption>
                    {{ i18n.documents_activity_description }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <a
                    target="_blank"
                    :href="report.files_fiscal_report"
                    download
                  >
                    <q-btn
                      unelevated
                      no-caps
                      icon="las la-download"
                      flat
                      color="primary"
                    />
                  </a>
                </q-item-section>
              </q-item>

              <!-- Cessions -->
              <q-item>
                <q-item-section avatar>
                  <q-avatar
                    color="pastel-blue"
                    text-color="dark"
                  >
                    <q-icon name="compare_arrows" />
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-bold">
                    {{ i18n.documents_sales_label }}
                  </q-item-label>
                  <q-item-label caption>
                    {{ i18n.documents_sales_description }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <a
                    target="_blank"
                    :href="report.files_sales"
                    download
                  >
                    <q-btn
                      unelevated
                      no-caps
                      icon="las la-download"
                      flat
                      color="primary"
                    />
                  </a>
                </q-item-section>
              </q-item>

              <!-- Attestation de déclaration -->
              <q-item>
                <q-item-section avatar>
                  <q-avatar
                    color="pastel-blue"
                    text-color="dark"
                  >
                    <q-icon name="las la-stamp" />
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-bold">
                    {{ i18n.documents_title }}
                  </q-item-label>
                  <q-item-label caption>
                    {{ i18n.documents_certificate_description }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <a
                    target="_blank"
                    :href="report.files_certificate"
                    download
                  >
                    <q-btn
                      unelevated
                      no-caps
                      icon="las la-download"
                      flat
                      color="primary"
                    />
                  </a>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>

        <!-- Help and links -->
        <q-card class="q-mt-md">
          <q-card-section class="row items-start justify-space-between">
            <div class="col">
              <span class="q-card-title">{{ i18n.links_title }}</span>
            </div>
          </q-card-section>

          <q-card-section class="q-pt-none">
            <q-list>
              <q-item>
                <q-item-section avatar>
                  <q-avatar
                    color="pastel-yellow"
                    text-color="dark"
                  >
                    <q-icon name="las la-file-pdf" />
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-bold">
                    {{ i18n.links_guide_label }}
                  </q-item-label>
                  <q-item-label caption>
                    {{ i18n.links_guide_description }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <a
                    href="/guides/guide_declaration.pdf"
                    download
                  >
                    <q-btn
                      flat
                      unelevated
                      no-caps
                      icon="las la-download"
                      color="primary"
                    />
                  </a>
                </q-item-section>
              </q-item>

              <q-item>
                <q-item-section avatar>
                  <q-avatar
                    color="pastel-yellow"
                    text-color="dark"
                  >
                    <q-icon
                      name="link"
                      class="rotate-135"
                    />
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-bold">
                    {{ i18n.links_help_label }}
                  </q-item-label>
                  <q-item-label caption>
                    {{ i18n.links_help_description }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    flat
                    unelevated
                    no-caps
                    icon="las la-external-link-alt"
                    color="primary"
                    href="https://comptacrypto.com/documentation/"
                    target="_blank"
                  />
                </q-item-section>
              </q-item>

              <!-- <q-item>
          <q-item-section avatar>
            <q-avatar color="primary" text-color="dark">
              <q-icon name="las la-question-circle" />
            </q-avatar>
          </q-item-section>
          <q-item-section>
            <q-item-label class="text-bold">Support client</q-item-label>
            <q-item-label caption>Contacter notre service client</q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-btn flat unelevated no-caps icon="fas fa-arrow-up-right-from-square" color="primary" />
          </q-item-section>
        </q-item> -->

              <q-item>
                <q-item-section avatar>
                  <q-avatar
                    color="pastel-yellow"
                    text-color="dark"
                  >
                    <q-icon
                      name="link"
                      class="rotate-135"
                    />
                  </q-avatar>
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-bold">
                    {{ i18n.links_2086_label }}
                  </q-item-label>
                  <q-item-label caption>
                    impots.gouv.fr
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    flat
                    unelevated
                    no-caps
                    icon="las la-external-link-alt"
                    color="primary"
                    href="https://www.impots.gouv.fr"
                    target="_blank"
                  />
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>
      </template>
    </template>
  </q-page>
</template>
<script>
import Layout from '@/layouts/workspace.vue';
import { usePoll } from '@inertiajs/vue3';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    Link,
  },
  layout: Layout,
  props: {
    i18n: {
      type: String,
      required: true,
    },
    report_years_available: {
      type: Array,
      required: true,
    },
    report_years_selected: {
      type: String,
      required: true,
    },
    bnc_recognition_selected: {
      type: String,
      required: false,
      default: 'at_disposal',
    },
    report_create_action: {
      type: String,
      required: true,
    },
    report: {
      type: Object,
      required: false,
    },
    step_import_action: {
      type: String,
      required: true,
    },
    step_import_status: {
      type: Object,
      required: true,
    },
    step_subscription_action: {
      type: String,
      required: true,
    },
    step_subscription_status: {
      type: Object,
      required: true,
    },
  },
  data() {
    const poll = usePoll(
      1000,
      {
        only: ["report"],
        data: { ajax_bar_skip: '' },
      },
      { autoStart: this.report != void 0 && this.report?.status != 'success' }
    );

    return {
      report_disclaimer_accepted: false,
      report_disclaimer_confirm_accounts: false,
      report_disclaimer_confirm_categorization: false,
      bnc_at_reception: this.bnc_recognition_selected === 'at_reception',
      report_polling_start: poll.start,
      report_polling_stop: poll.stop,
    };
  },
  computed: {
    report_requirements_valid: function() {
      return this.step_import_status.state == 'done' && this.step_subscription_status.state == 'done'
    },
    report_button_display_enabled: function() {
      return this.report_disclaimer_confirm_accounts && this.report_disclaimer_confirm_categorization
    },
  },
  watch: {
    bnc_recognition_selected: {
      handler: function(new_value) {
        this.bnc_at_reception = new_value === 'at_reception';
      },
    },
    'report.status': {
      handler: function(new_value) {
        if (new_value == 'success') {
          this.report_polling_stop();
        } else if (new_value == 'pending') {
          this.report_polling_start();
        } else if (new_value == 'processing') {
          this.report_polling_start();
        }
      },
    },
  },
  mounted() {
    // Only create report if it doesn't exist yet
    if (this.report_requirements_valid && !this.report) {
      this.report_create(this.report_years_selected);
    }
  },
  methods: {
    report_years_select: function(new_value) {
      // Just navigate to the selected year, don't auto-calculate
      this.$inertia.get(
        '',
        { year: new_value },
        {
          preserveState: true,
          preserveScroll: true,
        }
      );
    },
    report_create(year) {
      this.$inertia.post(
        this.report_create_action,
        {
          year: year,
          bnc_recognition: this.bnc_at_reception ? 'at_reception' : 'at_disposal',
        },
        {
          preserveState: true,
          preserveScroll: true,
        }
      );
    },
  },
};
</script>
<style scoped>
.report-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  min-height: 320px;
}

.overlay-content {
  width: 100%;
  text-align: left;
  padding: 2rem;
  border-radius: 8px;
}

#report_section {
  position: relative;
}

.report-section-with-disclaimer {
  min-height: 320px;
  margin-bottom: 16px;
}
</style>
